import { useTranslation } from 'core/i18n/i18n';
import { useMemo } from 'react';
import {
    ConnectionStatus,
    ConnectionStatusCodes,
} from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/types/active-directory.type';
import { ActiveDirectoryStatusBase } from './components';

interface ActiveDirectoryConnectionStatusProps {
    status: ConnectionStatus;
    errorMessage?: string;
    lastAttempt?: string;
}

export const ActiveDirectoryConnectionStatus: React.FC<ActiveDirectoryConnectionStatusProps> = ({
    status,
    errorMessage,
    lastAttempt,
}) => {
    const { t } = useTranslation();

    const iconName = useMemo(
        () =>
            status === ConnectionStatusCodes.SUCCESS || status === ConnectionStatusCodes.LAST_CONNECTION_SUCCESS
                ? 'connection_on'
                : 'connection_off',
        [status]
    );

    const iconStatus = useMemo(() => {
        switch (status) {
            case ConnectionStatusCodes.SUCCESS:
            case ConnectionStatusCodes.LAST_CONNECTION_SUCCESS:
                return 'positive';
            case ConnectionStatusCodes.PENDING:
            case ConnectionStatusCodes.TESTING_CONNECTION:
                return 'warning';
            case ConnectionStatusCodes.ERROR:
            case ConnectionStatusCodes.TIMEOUT:
            case ConnectionStatusCodes.DISCONNECTED:
            case ConnectionStatusCodes.LAST_CONNECTION_FAILED:
                return 'alarm';
            case ConnectionStatusCodes.NOT_CONFIGURED:
            case ConnectionStatusCodes.DEACTIVATED:
                return undefined;
            default:
                return undefined;
        }
    }, [status]);

    const label = useMemo(() => {
        switch (status) {
            case ConnectionStatusCodes.SUCCESS:
                return t(`settings.integrations.active-directory.host.info.connection.status.success`);
            case ConnectionStatusCodes.PENDING:
                return t(`settings.integrations.active-directory.host.info.connection.status.pending`);
            case ConnectionStatusCodes.TESTING_CONNECTION:
                return t(`settings.integrations.active-directory.host.info.connection.status.testingConnection`);
            case ConnectionStatusCodes.ERROR:
                return t(`settings.integrations.active-directory.host.info.connection.status.error`);
            case ConnectionStatusCodes.TIMEOUT:
                return t(`settings.integrations.active-directory.host.info.connection.status.timeout`);
            case ConnectionStatusCodes.DISCONNECTED:
                return t(`settings.integrations.active-directory.host.info.connection.status.disconnected`);
            case ConnectionStatusCodes.DEACTIVATED:
                return t(`settings.integrations.active-directory.host.info.connection.status.deactivated`);
            case ConnectionStatusCodes.NOT_CONFIGURED:
                return t(`settings.integrations.active-directory.host.info.connection.status.notConfigured`);
            case ConnectionStatusCodes.LAST_CONNECTION_SUCCESS:
                return t(`settings.integrations.active-directory.host.info.connection.status.lastConnectionSuccess`);
            case ConnectionStatusCodes.LAST_CONNECTION_FAILED:
                return t(`settings.integrations.active-directory.host.info.connection.status.lastConnectionFailed`);
            default:
                return '';
        }
    }, [status, t]);

    return (
        <ActiveDirectoryStatusBase
            status={status}
            iconName={iconName}
            iconStatus={iconStatus}
            label={label}
            errorMessage={errorMessage}
            lastAttempt={lastAttempt}
        />
    );
};
